import { useCallback, useEffect, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { TGTGBagAdditionMethod } from "analytics/events";
import { IntlMessageId } from "shared/lang";
import { Button } from "ui/Button/Button";
import { Form } from "ui/Form/Form";
import { Input } from "ui/Input/Input";
import { Modal } from "ui/Modal/Modal";
import { BodyM, HeaderS } from "ui/Typography/Typography";

import { useAddProductToBag } from "../hooks/useBagProductActions";
import { GENERATED_SKU_PREFIX, useTGTGBagStore } from "../stores/useTGTGBagStore";

interface ProductNotListedModalProps {
  isOpen: boolean;
}

function ProductNotListedModal({ isOpen }: ProductNotListedModalProps) {
  const intl = useIntl();

  const { productToAdd, setProductToAdd, setShowProductNotListedModal } = useTGTGBagStore(
    useShallow((state) => ({
      productToAdd: state.productToAdd,
      setProductToAdd: state.setProductToAdd,
      setShowProductNotListedModal: state.setShowProductNotListedModal,
    })),
  );
  const addProductToBag = useAddProductToBag();
  const [productName, setProductName] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [formError, setFormError] = useState({ name: false, price: false });

  useEffect(() => {
    if (productToAdd?.productName) {
      setProductName(productToAdd.productName);
    }
  }, [productToAdd?.productName]);

  const handleCloseModal = useCallback(() => {
    setProductToAdd(null);
    setProductName("");
    setProductPrice("");
    setShowProductNotListedModal(false);
  }, [setProductToAdd, setShowProductNotListedModal]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isNameValid = productName.trim() !== "";
    const price = parseFloat(productPrice);
    const isPriceValid = !Number.isNaN(price) && price > 0;

    if (!isNameValid || !isPriceValid) {
      setFormError({ name: !isNameValid, price: !isPriceValid });
      return;
    }
    const id = productToAdd?.id || crypto.randomUUID();
    const productSku = productToAdd?.productSku || `${GENERATED_SKU_PREFIX}${id.slice(0, 8)}`;
    addProductToBag(
      {
        id,
        ean: productToAdd?.ean ?? "",
        productSku,
        productName,
        productImageUrl: productToAdd?.productImageUrl ?? null,
        productPrice: price,
        countryOfOriginCode: productToAdd?.countryOfOriginCode ?? null,
        countryOfOriginName: productToAdd?.countryOfOriginName ?? null,
        productClassification: productToAdd?.productClassification ?? null,
      },
      TGTGBagAdditionMethod.Manual,
    );
    handleCloseModal();
  };

  const translatedLabels: { title: IntlMessageId; body: IntlMessageId } =
    productToAdd?.productName && !productToAdd?.productPrice
      ? {
          title: "components.product-not-listed-modal.no-price.title",
          body: "components.product-not-listed-modal.no-price.body",
        }
      : {
          title: "components.product-not-listed-modal.title",
          body: "components.product-not-listed-modal.body",
        };

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={() => {}}
    >
      <Modal.Content marginX="s300">
        <Form w="100%" onSubmit={handleFormSubmit}>
          <Modal.Header textAlign="center" pb="s100">
            <HeaderS>
              <FormattedMessage id={translatedLabels.title} />
            </HeaderS>
          </Modal.Header>
          <Modal.Body textAlign="center">
            <BodyM color="grey.700" sx={{ whiteSpace: "pre-wrap" }}>
              <FormattedMessage id={translatedLabels.body} values={{ br: <br /> }} />
            </BodyM>
            <Form.Field mt="s300" isInvalid={formError.name}>
              <Input
                h="48px"
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "components.product-not-listed-modal.placeholder.product-name",
                })}
                autoComplete="off"
                isReadOnly={!!productToAdd?.productName}
              />
              {formError.name && (
                <Form.ErrorMessage>
                  <FormattedMessage id="components.product-not-listed-modal.error" />
                </Form.ErrorMessage>
              )}
            </Form.Field>
            <Form.Field mt="s100" isInvalid={formError.price}>
              <Input
                h="48px"
                type="number"
                step="any" // Allows decimal values
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                placeholder={intl.formatMessage({
                  id: "components.product-not-listed-modal.placeholder.product-price",
                })}
                autoComplete="off"
              />
              {formError.price && (
                <Form.ErrorMessage>
                  <FormattedMessage id="components.product-not-listed-modal.error" />
                </Form.ErrorMessage>
              )}
            </Form.Field>
          </Modal.Body>
          <Modal.Footer flexDir="column">
            <Button
              type="submit"
              isFullWidth
              variant="primary"
              size="lg"
              data-testid="yes-no-modal-yes-button"
            >
              <FormattedMessage id="components.product-not-listed-modal.save-item" />
            </Button>
            <Button
              mt="s100"
              onClick={handleCloseModal}
              isFullWidth
              variant="secondary"
              size="lg"
              data-testid="yes-no-modal-no-button"
            >
              <FormattedMessage id="generic.actions.back" />
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
}

export default ProductNotListedModal;
